// useActiveShift.js
// The ONE subscription to "which shift is this surface working with?"
//
// Replaces 16 hand-rolled `where("isActive","==",true)` → `docs[0]` queries
// that each independently coin-flipped once two events ran at the same time.
// Resolution policy lives in utils/shiftScope.js — this hook is just the
// Firestore plumbing around it. See that file's header for the rules.
//
// Returns:
//   shift        resolved shift doc, or null
//   loading      first snapshot not yet in
//   candidates   every active shift (length > 1 means concurrent events)
//   needsChoice  ambiguous AND unresolved — caller must prompt, never guess
//   bind(shift)  pin this device to a shift (persists to localStorage)
//   unbind()     drop the pin
//
// Auth: signs in anonymously if nobody is signed in, so public surfaces
// (/join, /order, /overlay/*) can use the same hook as staff routes. Mirrors
// useCurrentShiftOrders — wait for the persisted session to restore first,
// because signInAnonymously at mount races the restore and would replace a
// signed-in staff session for the whole browser profile.
import { useCallback, useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { onAuthStateChanged, signInAnonymously } from "firebase/auth";
import { auth, db } from "./../firebase";
import {
  getBoundShiftId,
  readShiftParam,
  resolveShift,
  setBoundShift,
  clearBoundShift,
  shiftLabel,
} from "./../utils/shiftScope";

export default function useActiveShift(options = {}) {
  const {
    // Overlay browser sources and QR deep links carry their scope in the URL.
    // Staff views pass nothing and fall through to the device binding.
    param = null,
    // Test shifts are their own population (Lab rails). Callers that must not
    // see them pass false — matches the existing flag-and-filter rule.
    includeTestShifts = true,
  } = options;

  const [authed, setAuthed] = useState(!!auth.currentUser);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Bumped by bind/unbind so resolution re-runs without a Firestore round-trip.
  const [bindTick, setBindTick] = useState(0);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u || auth.currentUser) { setAuthed(true); return; }
      signInAnonymously(auth).catch(() => {});
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!authed) return;
    // No limit(1) — the whole point is seeing every concurrent queue.
    const q = query(collection(db, "shifts"), where("isActive", "==", true));
    return onSnapshot(
      q,
      (snap) => {
        setShifts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => { setShifts([]); setLoading(false); }
    );
  }, [authed]);

  const visible = useMemo(
    () => (includeTestShifts ? shifts : shifts.filter(s => !s._isTestData)),
    [shifts, includeTestShifts]
  );

  const resolved = useMemo(() => {
    const p = param || readShiftParam();
    return resolveShift(visible, { param: p, boundId: getBoundShiftId() });
    // bindTick is a deliberate dependency: bind() mutates localStorage, which
    // useMemo cannot observe on its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, param, bindTick]);

  const bind = useCallback((shift) => {
    if (!shift?.id) return;
    setBoundShift(shift.id, shiftLabel(shift));
    setBindTick(t => t + 1);
  }, []);

  const unbind = useCallback(() => {
    clearBoundShift();
    setBindTick(t => t + 1);
  }, []);

  // A device that opened or picked a queue should stay on it without the
  // operator thinking about it. Self-heal the binding whenever resolution
  // succeeded but the stored pin disagrees — including the single-shift case,
  // which quietly re-points a device left bound to a closed shift.
  useEffect(() => {
    if (!resolved.shift) return;
    if (getBoundShiftId() === resolved.shift.id) return;
    setBoundShift(resolved.shift.id, shiftLabel(resolved.shift));
  }, [resolved.shift]);

  return {
    shift: resolved.shift,
    shiftId: resolved.shift?.id || null,
    loading,
    candidates: visible,
    needsChoice: resolved.needsChoice,
    isConcurrent: visible.length > 1,
    reason: resolved.reason,
    bind,
    unbind,
  };
}
